import { makeAutoObservable } from 'mobx';

const STORAGE_KEY = 'digitiles-palette-collapse-v1';

/** Stable keys are independent of displayed headings and item order. */
export const paletteSections = {
  core: 'tiles:core',
  extensions: 'tiles:extensions',
  pending: 'tiles:pending-interfaces',
  singleTileChips: 'tiles:single-tile-chips',
  ungroupedChips: 'chips:ungrouped',
} as const;
export const chipGroupSection = (folder: string) => `chips:folder:${folder}`;

export class PaletteExpansionModel {
  private collapsed = new Set<string>();
  private revealed = new Set<string>();

  constructor() {
    try {
      const saved: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
      if (Array.isArray(saved))
        this.collapsed = new Set(
          saved.filter((id): id is string => typeof id === 'string'),
        );
    } catch {
      /* Keep defaults when storage is unavailable or malformed. */
    }
    makeAutoObservable(this, {}, { autoBind: true });
  }

  isExpanded(id: string) {
    return !this.collapsed.has(id) || this.revealed.has(id);
  }

  toggle(id: string) {
    const expanded = this.isExpanded(id);
    this.revealed.delete(id);
    if (expanded) this.collapsed.add(id);
    else this.collapsed.delete(id);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.collapsed]));
    } catch {
      /* Keep the in-memory preference. */
    }
  }

  renameSection(from: string, to: string) {
    if (this.collapsed.has(from)) this.collapsed.add(to);
    else this.collapsed.delete(to);
    if (this.revealed.has(from)) this.revealed.add(to);
    else this.revealed.delete(to);
    this.collapsed.delete(from);
    this.revealed.delete(from);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.collapsed]));
    } catch {
      /* Keep the in-memory preference. */
    }
  }

  /** Tutorial cues reveal content without overwriting the user's saved preference. */
  reveal(id: string) {
    this.revealed.add(id);
  }
}
